import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save, Send, Sparkles, Loader2, Info, AlertTriangle, MonitorSmartphone } from "lucide-react";
import { getHorse, getPreview, generateWriteups, submitListing, prefillHorse } from "@/lib/api";
import { statusLabel, statusTone } from "@/lib/status";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import AdPreview from "@/components/AdPreview";
import { AdPhotos } from "@/components/AdPhotos";
import { AdPapers } from "@/components/AdPapers";
import { HeadlineField } from "@/components/adtemplate/HeadlineField";
import { SaleDatesPanel } from "@/components/adtemplate/SaleDatesPanel";
import { PreviewAdDialog } from "@/components/adtemplate/PreviewAdDialog";
import { AiVersions } from "@/components/adtemplate/AiVersions";
import { ColourField, BreedField } from "@/components/adtemplate/ColourField";
import { GenderSelect } from "@/components/adtemplate/GenderSelect";
import { AdOrder } from "@/components/adtemplate/AdOrder";
import { adSequence, saveSequence } from "@/lib/adMedia";
import { AdStatusPill } from "@/components/AdTemplateStatus";
import { Field, DisciplineField, SaveStatus, HeightSelect } from "@/components/adtemplate/AdFields";
import { normalizeHeight } from "@/lib/format";
import { SectionNav, useActiveSection, scrollToSection, scrollToEl, NAV_OFFSET } from "@/components/adtemplate/SectionNav";
import { useAutosave, applyDirty, readPending } from "@/hooks/useAutosave";
import { useAuth } from "@/lib/auth";
import { isAdminRole } from "@/lib/access";

const HORSE_FIELDS = [
  ["horse_name", "Horse's Barn Name"], ["year_born", "Year Born"],
  ["age", "Age"],
  ["height_now", "Current Height (HH)"], ["height_maturity", "Expected Mature Height (HH)"],
  ["registered_name", "Registered Name"], ["registration_number", "Registration #"],
  ["sire", "Sire"], ["dam", "Dam"],
];
const YEAR = /^(19|20)\d\d$/;
// Year Born is the source of truth for Age.
const ageFrom = (hi) => {
  const y = [hi.year_born, hi.age].map((v) => String(v ?? "").trim()).find((v) => YEAR.test(v));
  if (y) { const a = thisYear() - Number(y); return a >= 0 && a <= 45 ? a : null; }
  const n = Number(String(hi.age ?? "").trim());
  return /^\d{1,2}$/.test(String(hi.age ?? "").trim()) ? n : null;
};
// 5+ year olds: mature height = current height unless the seller set it themselves.
const matureAuto = (hi, patch) => {
  const n = { ...hi, ...patch };
  const now = normalizeHeight(n.height_now);
  if (n.height_maturity_manual || !now) return {};
  if ((ageFrom(n) ?? 0) >= 5 && (!String(n.height_maturity ?? "").trim() || n.height_maturity_auto)) return { height_maturity: now, height_maturity_auto: true };
  return {};
};
const SELLER_FIELDS = [
  ["customer_name", "Seller Name"], ["customer_phone", "Seller Phone"],
  ["horse_location_city", "Location City"], ["horse_location_state", "Province / State"],
  ["country", "Country"],
];
const SALE_FIELDS = [
  ["sale_start_date", "Bidding Start (YYYY-MM-DD)"], ["sale_close_date", "Bidding Close (YYYY-MM-DD)"],
  ["sale_close_time", "Close Time (optional override)"], ["price", "Price"],
];
const AD_SALE_FIELDS = [["reserve_amount", "Reserve Amount"]];
const AD_EDITABLE = ["draft", "changes_requested", "submitted", "approved"];
const fmtMoney = (v) => {
  const [i, d] = String(v).replace(/[^\d.]/g, "").split(".");
  const int = (i || "").replace(/^0+(?=\d)/, "").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return d !== undefined ? `${int}.${d.slice(0, 2)}` : int;
};
const SECTIONS_AD = [["sec-horse", "Horse Info"], ["sec-seller", "Seller & Trainer"], ["sec-sale", "Sale Info"], ["sec-description", "Description"], ["sec-media", "Photos & Videos"]];
const SECTIONS_LISTING = SECTIONS_AD.slice(0, 4);
const IDS_AD = SECTIONS_AD.map(([s]) => s);
const IDS_LISTING = SECTIONS_LISTING.map(([s]) => s);
const PREFIX = { horse_info: "field-horse-", seller_info: "field-seller-", sale_info: "field-sale-" };
// Page order (first missing field gets the scroll). Mirrors ad_template.required_for on the server.
const REQUIRED = [
  ["horse_info", "disciplines", "Discipline / Type"], ["horse_info", "discipline_other", "Other Discipline", (h) => (h.horse_info?.disciplines || []).includes("other")],
  ["horse_info", "registered", "Registered"], ["horse_info", "horse_name", "Horse's Barn Name"], ["horse_info", "age", "Age or Year Born"],
  ["horse_info", "height_now", "Current Height"], ["horse_info", "height_maturity", "Expected Mature Height"],
  ["horse_info", "registered_name", "Registered Name", (h) => isRegistered(h)], ["horse_info", "registration_number", "Registration #", (h) => isRegistered(h)],
  ["horse_info", "sire", "Sire (or N/A)", (h) => !isNotRegistered(h)], ["horse_info", "dam", "Dam (or N/A)", (h) => !isNotRegistered(h)],
  ["horse_info", "gender", "Gender"], ["horse_info", "horse_color", "Colour"], ["horse_info", "breed", "Breed"],
  ["seller_info", "customer_name", "Seller Name"], ["seller_info", "customer_phone", "Seller Phone"],
  ["seller_info", "horse_location_city", "Location City"], ["seller_info", "horse_location_state", "Province / State"], ["seller_info", "country", "Country"],
  ["seller_info", "trainer_name", "Trainer Name", (h) => hasTrainer(h)], ["seller_info", "trainer_phone", "Trainer Phone", (h) => hasTrainer(h)],
  ["seller_info", "trainer_email", "Trainer Email", (h) => hasTrainer(h)],
  ["sale_info", "reserve_amount", "Reserve Amount"], ["sale_info", "coggins", "Coggins Provided by Seller"],
  ["sale_info", "vet_check", "Vet Check Provided by Seller"], ["sale_info", "xrays_provided", "X-Rays Provided by Seller"],
  ["write_up", "original", "Description"], ["media", "photos", "At least 1 photo"],
  ["horse_info", "ad_catchy_header", "Catchy Ad Headline"], ["horse_info", "headline_emoji", "Headline Emoji"],
];
const isRegistered = (h) => String(h.horse_info?.registered ?? "").trim().toLowerCase() === "yes";
const isNotRegistered = (h) => String(h.horse_info?.registered ?? "").trim().toLowerCase() === "no";
const hasTrainer = (h) => (h.seller_info?.has_trainer || (h.seller_info?.trainer_name ? "yes" : "")) === "yes";
const requiredFor = (h) => REQUIRED.filter(([, , , when]) => !when || when(h));
const NA_FIELDS = new Set(["sire", "dam"]);
const HEALTH_FIELDS = [["coggins", "Coggins Provided by Seller"], ["vet_check", "Vet Check Provided by Seller"], ["xrays_provided", "X-Rays Provided by Seller"]];
// Horse Workspace: sticky window header (56px) + workspace tabs (~52px) + this editor's own sticky bar.
const EMBED_OFFSET = 204;

function YesNoPick({ id, label, value, onChange, disabled, error }) {
  const v = { yes: "Yes", no: "No" }[String(value ?? "").trim().toLowerCase()];
  return (
    <div data-testid={id}>
      <span className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">{label} <span className="text-red-600">*</span></span>
      <div role="radiogroup" aria-label={label} className="flex gap-2">
        {["Yes", "No"].map((o) => (
          <button key={o} type="button" role="radio" aria-checked={v === o} disabled={disabled} data-testid={`${id}-${o.toLowerCase()}`} onClick={() => onChange(o)}
            className={`h-11 flex-1 rounded-full border text-sm font-semibold transition-colors duration-150 disabled:opacity-50 ${v === o ? "bg-amber-800 border-amber-800 text-white" : `bg-white text-stone-700 hover:border-amber-700 ${error ? "border-red-500" : "border-stone-300"}`}`}>{o}</button>
        ))}
      </div>
      {!v && <p className={`mt-1 text-xs ${error ? "text-red-600" : "text-stone-500"}`}>{error || "Not answered yet"}</p>}
    </div>
  );
}
const thisYear = () => new Date().getFullYear();
const filled = (v) => (Array.isArray(v) ? v.length > 0 : String(v ?? "").trim() !== "");
const targetFor = (s, k) => (s === "media" ? "sec-media" : s === "write_up" ? "writeup-original" : `${PREFIX[s]}${k}`);

function missingOf(h) {
  return requiredFor(h).filter(([s, k]) => {
    if (s === "media") return !(h.media || []).some((m) => m.kind === "image" && m.ad_order);
    if (k === "age") return !filled(h.horse_info?.age) && !filled(h.horse_info?.year_born);
    if (s === "write_up") return !filled(h.write_up?.original) && !filled(h.write_up?.final_edited);
    return !filled(h[s]?.[k]);
  }).map(([s, k, label]) => ({ section: s, key: k, label }));
}

function focusFirst(missing, root, offset) {
  const el = document.querySelector(`[data-testid="${targetFor(missing[0].section, missing[0].key)}"]`) || document.getElementById(targetFor(missing[0].section, missing[0].key));
  if (!el) return;
  scrollToEl(el, root, offset + 24);
  setTimeout(() => el.focus?.({ preventScroll: true }), 450);
}

const TRAINER_FIELDS = [["trainer_name", "Trainer Name"], ["trainer_phone", "Trainer Phone"], ["trainer_email", "Trainer Email"]];

// Trainer section: hidden when the seller said there's no trainer, with an option to add one later.
function TrainerBlock({ si, locked, onChange, grid }) {
  const has = (si?.has_trainer || (si?.trainer_name ? "yes" : "")) === "yes";
  if (!has) {
    return (
      <button type="button" data-testid="ad-add-trainer" disabled={locked} onClick={() => onChange({ has_trainer: "yes", trainer_is_seller: false })}
        className="mt-4 min-h-[44px] px-4 rounded-full border border-dashed border-stone-400 text-sm font-semibold text-stone-700 disabled:opacity-50">+ Add a trainer</button>
    );
  }
  return (
    <div data-testid="ad-trainer-section" className="mt-4 space-y-2">
      <div className="flex items-center justify-between"><h3 className="text-sm font-semibold text-stone-800">Trainer</h3>
        {!locked && <button type="button" data-testid="ad-remove-trainer" onClick={() => onChange({ has_trainer: "no", trainer_is_seller: false, trainer_name: "", trainer_phone: "", trainer_email: "" })}
          className="text-xs text-stone-500 underline">No trainer</button>}</div>
      {grid(TRAINER_FIELDS)}
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} data-testid={id} className="scroll-mt-[150px] bg-white border border-stone-200 rounded-2xl p-4 sm:p-5">
      <h2 className="font-display text-base md:text-lg font-bold text-stone-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

// The ONE Horse Ad Template editor — the seller page and the Pipeline Horse Workspace both render it on the same horse record.
// embedded: shown inside the Horse Workspace (admin); renderHeader: page header for the standalone seller page.
export function AdTemplateEditor({ horseId: id, embedded = false, renderHeader, onLoadError, onDirtyChange }) {
  const [horse, setHorse] = useState(null);
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [root, setRoot] = useState(null);
  const offset = embedded ? EMBED_OFFSET : NAV_OFFSET;
  const rootRef = useCallback((node) => { if (embedded && node) setRoot(node.closest("[data-ad-scroll]")); }, [embedded]);

  const refreshPreview = useCallback(async () => {
    try { setPreview(await getPreview(id)); } catch { /* ignore */ }
  }, [id]);
  const autosave = useAutosave(id, setHorse, refreshPreview);
  const { user } = useAuth();
  const isAdmin = isAdminRole(user);
  const { queue, flush } = autosave;
  useEffect(() => { onDirtyChange?.(autosave.status !== "saved"); }, [autosave.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdTemplate = !!horse?.application_id;
  // Admins in the Workspace may correct a posted ad; sellers stay locked after posting (enforced by the API too).
  const adminEdit = embedded && isAdmin;
  const sellerEditable = (h) => (h.application_id ? AD_EDITABLE : ["draft", "changes_requested"]).includes(h.listing_status);
  const locked = !!(horse && horse.listing_status && !adminEdit && !sellerEditable(horse));
  const postedCorrection = !!(horse && adminEdit && horse.listing_status && !sellerEditable(horse));
  // Submit for Review is the seller's action; admins approve or request changes from the Workspace instead.
  const canSubmit = !embedded && !locked && ["draft", "changes_requested", undefined, null].includes(horse?.listing_status);
  const sections = isAdTemplate ? SECTIONS_AD : SECTIONS_LISTING;
  const [active, setActive] = useActiveSection(isAdTemplate ? IDS_AD : IDS_LISTING, flush, root, offset);

  useEffect(() => {
    (async () => {
      let h;
      try { h = await getHorse(id); } catch {
        setLoadFailed(true);
        onLoadError?.();
        return;
      }
      if (h.application_id) {
        try { h = await prefillHorse(id); } catch { /* keep loaded data */ }
      }
      const pending = readPending(id);
      const editable = adminEdit || sellerEditable(h);
      if (pending?.d && editable && pending.at > (h.last_modified_date || "")) {
        h = applyDirty(h, pending.d);
        queue(pending.d);
      } else if (pending) localStorage.removeItem(`bahs-adtpl-${id}`);
      const hi = h.horse_info || {};
      if (h.application_id && editable && !filled(hi.year_born) && /^\d{1,2}$/.test(String(hi.age ?? "").trim())) {
        const patch = { year_born: String(thisYear() - Number(hi.age)), year_born_auto: true };
        h = { ...h, horse_info: { ...hi, ...patch } };
        queue({ horse_info: patch });
      }
      if (editable) {
        const cur = h.horse_info || {};
        const fix = {};
        const a = ageFrom(cur);
        if (a != null && String(cur.age ?? "").trim() !== String(a) && (YEAR.test(String(cur.age ?? "").trim()) || YEAR.test(String(cur.year_born ?? "").trim()))) fix.age = String(a);
        Object.assign(fix, matureAuto(cur, fix));
        if (Object.keys(fix).length) { h = { ...h, horse_info: { ...cur, ...fix } }; queue({ horse_info: fix }); }
      }
      setHorse(h);
      refreshPreview();
    })();
  }, [id, refreshPreview, queue]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (section, patch) => {
    const next = { ...horse, [section]: { ...(horse[section] || {}), ...patch } };
    setHorse((h) => ({ ...h, [section]: { ...(h[section] || {}), ...patch } }));
    queue({ [section]: patch });
    // Also drop errors for fields that are no longer required (e.g. Registered switched to No).
    const still = new Set(requiredFor(next).map(([s, k]) => `${s}.${k}`));
    setErrors((e) => e.filter((m) => !(m.section === section && Object.keys(patch).includes(m.key)) && !(m.key === "age" && ("year_born" in patch || "age" in patch))
      && (m.section === "media" || still.has(`${m.section}.${m.key}`))));
  };
  const setHorseField = (k, v) => {
    const hi = horse.horse_info || {};
    const patch = { [k]: v };
    if (k === "age" && hi.year_born_auto && /^\d{1,2}$/.test(v.trim())) patch.year_born = String(thisYear() - Number(v));
    if (k === "year_born") {
      patch.year_born_auto = false;
      const age = thisYear() - Number(v);
      if (/^\d{4}$/.test(v.trim()) && age >= 0 && age <= 45) patch.age = String(age);
    }
    if (k === "height_maturity") patch.height_maturity_manual = true;
    if (k === "height_maturity") patch.height_maturity_auto = false;
    else Object.assign(patch, matureAuto(hi, patch));
    change("horse_info", patch);
  };
  const setWriteUp = (key, value) => {
    setHorse((h) => ({ ...h, write_up: { ...(h.write_up || {}), [key]: value } }));
    queue({ [key === "original" ? "write_up_original" : "write_up_final"]: value });
    if (key === "original") setErrors((e) => e.filter((m) => m.section !== "write_up"));
  };
  const applyVersion = (n, text) => {
    setHorse((h) => ({ ...h, write_up: { ...(h.write_up || {}), final_edited: text, selected_version: n } }));
    queue({ write_up_final: text, selected_version: n });
    flush();
    toast.success(`AI Version ${n} copied to the Final Write-Up`);
  };
  const onMedia = (media) => { setHorse((h) => ({ ...h, media })); setErrors((e) => e.filter((m) => m.section !== "media")); refreshPreview(); };
  const jump = (sid) => { setActive(sid); flush(); scrollToSection(sid, root, offset); };
  const errFor = (s, k) => (errors.some((m) => m.section === s && m.key === k) ? "Required" : null);

  const saveNow = async () => {
    if (await flush()) toast.success("Progress saved");
    else toast.error("Unable to save — your changes are kept on this device. Tap Retry.");
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try { if (!locked) await flush(); await refreshPreview(); } finally { setPreviewLoading(false); }
  };

  const generate = async (regenerate) => {
    setGenerating(true);
    try {
      if (!(await flush())) throw new Error("save");
      const wu = await generateWriteups(id, regenerate === true ? { regenerate: true } : {});
      // The endpoint returns ONLY the write_up block — merge it, never replace the listing.
      setHorse((h) => ({ ...h, write_up: { ...(h.write_up || {}), ...wu } }));
      refreshPreview();
      toast.success("AI write-ups generated");
    } catch (e) {
      toast.error(e.message === "save" ? "Couldn't save your latest changes first — nothing was generated. Please retry." : (e.response?.data?.detail || "Generation failed — nothing was changed"));
    } finally { setGenerating(false); }
  };

  const showMissing = (missing) => {
    setErrors(missing);
    toast.error(`Please complete: ${missing.map((m) => m.label).join(", ")}`);
    focusFirst(missing, root, offset);
  };

  const submit = async () => {
    if (isAdTemplate) {
      const missing = missingOf(horse);
      if (missing.length) return showMissing(missing);
    }
    setSubmitting(true);
    try {
      if (!(await flush())) throw new Error("save");
      setHorse(await submitListing(id));
      setErrors([]);
      toast.success("Submitted for review");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.missing) showMissing(d.missing);
      else toast.error(e.message === "save" ? "Couldn't save your latest changes. Check your connection and try again." : (typeof d === "string" ? d : "Could not submit"));
    } finally { setSubmitting(false); }
  };

  if (!horse) {
    if (embedded) return <p data-testid="ad-template-loading" className="text-sm text-stone-600 p-2">{loadFailed ? "The Ad Template could not be loaded." : "Loading Ad Template…"}</p>;
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-amber-800 animate-spin" />
      </div>
    );
  }

  const reqKeys = new Set(requiredFor(horse).map(([s, k]) => `${s}.${k}`));
  const req = (s, k) => isAdTemplate && (reqKeys.has(`${s}.${k}`) || (s === "horse_info" && k === "year_born"));
  const naFixed = (s, k) => isAdTemplate && s === "horse_info" && ["registered_name", "registration_number"].includes(k) && isNotRegistered(horse);
  const fieldGrid = (section, fields, onSet) => {
    const one = ([k, label]) => { const F = k.startsWith("height_") ? HeightSelect : Field; return (
        <F key={k} label={label} testid={`${PREFIX[section]}${k}`} value={horse[section]?.[k]} onChange={(v) => onSet(k, v)} disabled={locked}
          required={req(section, k) && !naFixed(section, k)} error={errFor(section, k)} inputMode={["age", "year_born"].includes(k) ? "numeric" : undefined}
          na={isAdTemplate && section === "horse_info" && NA_FIELDS.has(k)} naFixed={naFixed(section, k)}
          hint={naFixed(section, k) ? "Not registered" : k === "year_born" && horse.horse_info?.year_born_auto ? "Calculated from Age — correct it if needed"
            : (k === "sire" || k === "dam") && isAdTemplate && isNotRegistered(horse) ? "Optional — not registered"
            : k === "height_maturity" ? (horse.horse_info?.height_maturity_auto ? "Filled from Current Height (5+ years old) — change it if needed" : "Your estimate for young horses, e.g. 15.2")
            : k === "height_now" ? "In hands, e.g. 14.3"
            : k === "reserve_amount" ? `$ ${horse.sale_info?.currency || "CAD"} · From your signed agreement. Changing it here only updates the ad note — your signed contract stays the same.` : undefined} />
      ); };
    const dam = fields.find(([k]) => k === "dam");
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map((f) => (f[0] === "dam" && fields.some(([k]) => k === "sire") ? null
          // Sire and Dam stay together, Dam directly below Sire.
          : f[0] === "sire" && dam ? <div key="pedigree" data-testid={`${PREFIX[section]}pedigree`} className="space-y-4">{one(f)}{one(dam)}</div> : one(f)))}
      </div>
    );
  };

  const barRow = (
    <div className={`${embedded ? "px-3" : "max-w-6xl mx-auto px-3 sm:px-8"} pt-2 flex flex-wrap items-center justify-between gap-2`}>
      {isAdTemplate ? <AdStatusPill status={horse.ad_template_status} testId="ad-template-status-badge" /> : (
        <Badge variant="outline" data-testid="listing-status-badge" className={`rounded-full ${statusTone(horse.listing_status)}`}>
          {statusLabel(horse.listing_status)}
        </Badge>
      )}
      <div className="flex items-center gap-2 ml-auto">
        {!locked && <SaveStatus status={autosave.status} onRetry={flush} />}
        {isAdTemplate && (
          <Button data-testid="preview-ad-button" onClick={openPreview} variant="outline" size="sm"
            className="h-9 rounded-full border-amber-800 text-amber-800 hover:bg-amber-50 font-semibold">
            <MonitorSmartphone className="h-4 w-4 mr-1.5" />Preview Ad
          </Button>
        )}
      </div>
    </div>
  );
  const bar = embedded
    ? <div data-testid="ad-template-embedded-bar" className="sticky top-[6.75rem] z-[4] bg-white border border-stone-200 rounded-2xl shadow-sm">{barRow}<SectionNav sections={sections} active={active} onJump={jump} /></div>
    : <div className="bg-white border-b border-stone-200">{barRow}<SectionNav sections={sections} active={active} onJump={jump} /></div>;

  const form = (
    <div className="space-y-5 min-w-0">
      {horse.listing_status === "changes_requested" && horse.review_note && (
        <div data-testid="changes-requested-note" className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <Info className="h-4 w-4 mt-0.5 text-amber-800 shrink-0" />
          <p className="text-amber-900"><strong>Changes requested:</strong> {horse.review_note}</p>
        </div>
      )}
      {locked && (
        <div data-testid="listing-locked-note" className="text-sm bg-stone-100 border border-stone-200 rounded-xl px-4 py-3 text-stone-600">
          {isAdTemplate ? "This ad has been posted and can no longer be edited." : <>This listing is <strong>{statusLabel(horse.listing_status)}</strong> and locked for editing.</>}
        </div>
      )}
      {postedCorrection && (
        <div data-testid="posted-correction-note" className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-900">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          {horse.listing_status === "published"
            ? <p><strong>Posted ad — admin corrections only.</strong> The seller can no longer edit. Your changes save to the shared horse record and update the website listing. The Facebook post is not changed or reposted. Corrections are recorded in History.</p>
            : <p><strong>{statusLabel(horse.listing_status)} — admin editing only.</strong> The seller can't edit at this stage. Your changes save to the shared horse record and nothing is posted or reposted.</p>}
        </div>
      )}
      {!embedded && isAdTemplate && !locked && ["submitted", "approved"].includes(horse.listing_status) && (
        <div data-testid="ad-submitted-note" className="text-sm bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-blue-900">
          <strong>{horse.listing_status === "submitted" ? "Ad Submitted — Awaiting Review." : "Ad Approved."}</strong>{" "}
          You can still update your information until the ad is posted. Changes save automatically and our team sees the latest version.
        </div>
      )}
      {isAdTemplate && !locked && !postedCorrection && (embedded
        ? <p data-testid="ad-template-intro" className="text-sm text-stone-600">This is the seller's Ad Template — the same record the seller edits. Changes save automatically and the seller sees them after refreshing.</p>
        : <p data-testid="ad-template-intro" className="text-sm text-stone-600">We've filled in what you already gave us. Review each section and add anything missing — your changes save automatically. When you're done, tap <strong>Submit for Review</strong>.</p>)}
      {errors.length > 0 && (
        <div data-testid="missing-fields-banner" className="flex items-start gap-2 text-sm bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <p><strong>Please complete before submitting:</strong> {errors.map((m) => m.label).join(", ")}</p>
        </div>
      )}

      <Section id="sec-horse" title="Horse Info">
        <div className="grid sm:grid-cols-2 gap-4">
          {isAdTemplate ? (
            <DisciplineField info={horse.horse_info} disabled={locked} error={errFor("horse_info", "disciplines")} otherError={errFor("horse_info", "discipline_other")} onChange={(p) => change("horse_info", p)} />
          ) : (
            <Field label="Discipline / Type" testid="field-horse-horse_type" value={horse.horse_info?.horse_type} onChange={(v) => setHorseField("horse_type", v)} disabled={locked} />
          )}
        </div>
        {isAdTemplate && (
          <div className="mt-4 sm:w-1/2">
            <YesNoPick id="field-horse-registered" label="Registered" value={horse.horse_info?.registered} disabled={locked} error={errFor("horse_info", "registered")}
              onChange={(v) => change("horse_info", { registered: v })} />
          </div>
        )}
        <div className="mt-4">{fieldGrid("horse_info", isAdTemplate ? HORSE_FIELDS : [...HORSE_FIELDS.slice(0, 3), ["registered", "Registered (Yes / No)"], ...HORSE_FIELDS.slice(3)], setHorseField)}</div>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="space-y-1">
            <label htmlFor="field-horse-gender" className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Gender{req("horse_info", "gender") && <span className="text-red-600"> *</span>}</label>
            <GenderSelect id="field-horse-gender" value={horse.horse_info?.gender} disabled={locked} error={errFor("horse_info", "gender")} onChange={(v) => setHorseField("gender", v)} />
            {errFor("horse_info", "gender") && <p className="text-xs text-red-600">{errFor("horse_info", "gender")}</p>}
          </div>
          <ColourField value={horse.horse_info?.horse_color} disabled={locked} required={req("horse_info", "horse_color")} error={errFor("horse_info", "horse_color")} onChange={(v) => setHorseField("horse_color", v)} />
          <BreedField value={horse.horse_info?.breed} disabled={locked} required={req("horse_info", "breed")} error={errFor("horse_info", "breed")} onChange={(v) => setHorseField("breed", v)} />
        </div>
      </Section>

      <Section id="sec-seller" title="Seller & Trainer">
        {fieldGrid("seller_info", SELLER_FIELDS, (k, v) => change("seller_info", { [k]: v }))}
        <TrainerBlock si={horse.seller_info} locked={locked} onChange={(p) => change("seller_info", p)}
          grid={(fields) => fieldGrid("seller_info", fields, (k, v) => change("seller_info", { [k]: v }))} />
      </Section>

      <Section id="sec-sale" title="Sale Info">
        {isAdTemplate && <SaleDatesPanel horseId={id} locked={locked} onSaved={refreshPreview} />}
        {fieldGrid("sale_info", isAdTemplate ? AD_SALE_FIELDS : SALE_FIELDS, (k, v) => change("sale_info", { [k]: k === "reserve_amount" ? fmtMoney(v) : v }))}
        {isAdTemplate && (
          <div data-testid="health-docs" className="mt-4 grid sm:grid-cols-3 gap-4">
            {HEALTH_FIELDS.map(([k, label]) => (
              <YesNoPick key={k} id={`field-sale-${k}`} label={label} value={horse.sale_info?.[k]} disabled={locked} error={errFor("sale_info", k)}
                onChange={(v) => { change("sale_info", { [k]: v }); }} />
            ))}
          </div>
        )}
      </Section>

      <Section id="sec-description" title={isAdTemplate ? "Description" : "Write-Up"}>
        <div className="space-y-4">
          {isAdTemplate && (
            <HeadlineField horseId={id} value={horse.horse_info?.ad_catchy_header} locked={locked} beforeGenerate={flush}
              onChange={(v) => change("horse_info", { ad_catchy_header: v })}
              emoji={horse.horse_info?.headline_emoji} emojiLocked={locked && !isAdmin} emojiRequired emojiError={!!errFor("horse_info", "headline_emoji")} headlineError={!!errFor("horse_info", "ad_catchy_header")}
              onEmoji={(v) => { change("horse_info", { headline_emoji: v }); flush(); }} />
          )}
          <div>
            <label htmlFor="writeup-original" className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">
              {isAdTemplate ? (embedded ? "Horse Description (seller's words)" : "Horse Description (your words)") : "Your Original Write-Up"}{isAdTemplate && <span className="text-red-600"> *</span>}
            </label>
            <Textarea id="writeup-original" data-testid="writeup-original" rows={6} value={horse.write_up?.original || ""} readOnly={locked}
              onChange={(e) => setWriteUp("original", e.target.value)} aria-invalid={!!errFor("write_up", "original")}
              placeholder="Describe your horse in your own words — the facts, temperament, training, achievements."
              className={`focus-visible:ring-amber-800 text-sm ${errFor("write_up", "original") ? "border-red-400" : "border-stone-200"}`} />
            {errFor("write_up", "original") && <p data-testid="writeup-original-error" className="mt-1 text-xs text-red-600">Required</p>}
          </div>
          <Button data-testid="generate-writeups-button" onClick={generate} disabled={generating || locked}
            variant="outline" className="rounded-full border-amber-800 text-amber-800 hover:bg-amber-50">
            {generating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
            Generate AI Write-Ups
          </Button>
          <AiVersions wu={horse.write_up} locked={locked} onUse={applyVersion} onRegenerate={() => generate(true)} regenerating={generating} />
          <div>
            <label htmlFor="writeup-final" className="block text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-1.5">Final Write-Up (used in ad)</label>
            <Textarea id="writeup-final" data-testid="writeup-final" rows={5} value={horse.write_up?.final_edited || ""} readOnly={locked}
              onChange={(e) => setWriteUp("final_edited", e.target.value)}
              className="border-stone-200 focus-visible:ring-amber-800 text-sm" />
          </div>
        </div>
      </Section>

      {isAdTemplate && (
        <Section id="sec-media" title="Photos & Videos">
          {errFor("media", "photos") && <p data-testid="ad-photos-error" className="mb-3 text-xs text-red-600 font-semibold">Add at least 1 photo shown in the ad.</p>}
          <AdPapers horseId={id} locked={locked} registeredHint={!!(horse.horse_info?.registered_name || horse.horse_info?.registration_number)} media={horse.media} onChange={onMedia}
            info={horse.horse_info} onApplyInfo={locked && !isAdmin ? null : (patch) => { change("horse_info", patch); flush(); }} />
          <div className="mt-8"><AdPhotos horseId={id} media={horse.media} locked={locked} onChange={onMedia} adminView={isAdmin} onReload={() => getHorse(id).then((h) => onMedia(h.media || [])).catch(() => {})} /></div>
          <div className="mt-8"><AdOrder horseId={id} media={horse.media} locked={locked} onChange={onMedia}
            onExclude={(m) => saveSequence(id, horse.media, adSequence(horse.media).filter((x) => x !== m), onMedia)} /></div>
        </Section>
      )}

      {!locked && (
        <div data-testid="ad-save-bar" className={`bottom-0 z-30 backdrop-blur border-t border-stone-200 flex flex-wrap items-center gap-2 sm:gap-3 ${embedded ? "fixed inset-x-0 sm:sticky sm:inset-x-auto py-2 sm:py-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] sm:-mx-5 px-4 sm:px-5 bg-[#F3F3F1]/95" : "sticky py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] -mx-4 px-4 bg-[#FAF8F5]/95 lg:mx-0 lg:px-0 lg:rounded-xl"}`}>
          <Button data-testid="save-listing-button" onClick={saveNow} disabled={autosave.status === "saving"}
            variant="outline" className={`rounded-full border-stone-300 text-stone-700 hover:bg-stone-50 ${embedded ? "h-9 sm:h-10" : ""}`}>
            {autosave.status === "saving" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {isAdTemplate ? "Save Progress" : "Save Draft"}
          </Button>
          {embedded && <SaveStatus status={autosave.status} onRetry={flush} />}
          {canSubmit && <Button data-testid="submit-listing-button" onClick={submit} disabled={submitting}
            className="rounded-full bg-amber-800 hover:bg-amber-900 text-white">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Submit for Review
          </Button>}
        </div>
      )}
    </div>
  );

  const dialog = isAdTemplate && <PreviewAdDialog open={previewOpen} onOpenChange={setPreviewOpen} horseId={id} preview={preview} media={horse.media} loading={previewLoading} textFirst={embedded} />;

  if (embedded) {
    return <div ref={rootRef} data-testid="workspace-ad-template" className="space-y-4">{bar}{form}{dialog}</div>;
  }
  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      {renderHeader?.({ bar, flush, horse, isAdTemplate })}
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-6 grid lg:grid-cols-2 gap-6">
        {form}
        <div className="lg:sticky lg:top-[150px] h-fit">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-800 mb-2">Ad Preview</div>
          <AdPreview preview={preview} showVersions={false} />
        </div>
      </main>
      {dialog}
    </div>
  );
}
